import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, CheckCircle, MapPin, XCircle } from 'lucide-react';
import { formatPhone, mapsUrl } from '@/lib/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  sent: 'bg-sky-100 text-sky-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  expired: 'bg-zinc-100 text-zinc-500',
};

export default function PublicQuoteView() {
  const { quoteId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data: resp } = await axios.get(`${API}/api/public/quotes/${quoteId}`);
        setData(resp);
      } catch { setError('Quote not found'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [quoteId]);

  const respondToQuote = async (action) => {
    setActionLoading(action);
    setMessage('');
    try {
      const { data: resp } = await axios.post(`${API}/api/public/quotes/${quoteId}/${action}`);
      setMessage(resp.message || `Quote ${action}ed.`);
      const { data: refreshed } = await axios.get(`${API}/api/public/quotes/${quoteId}`);
      setData(refreshed);
    } catch {
      setMessage('Unable to update this quote. Please call the business directly.');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center"><div className="animate-pulse w-full max-w-md h-96 bg-zinc-200 rounded-lg" /></div>;
  if (error) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center"><p className="text-zinc-500">{error}</p></div>;

  const { quote, business } = data;

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="border border-zinc-200 shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-md mb-3" style={{ backgroundColor: business?.color || '#09090b' }}>
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold font-heading" data-testid="public-quote-business">{business?.name}</h1>
              {business?.tagline && <p className="text-sm text-zinc-500">{business.tagline}</p>}
            </div>

            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-zinc-500 uppercase font-bold tracking-wider">Estimate</p>
                <p className="text-lg font-bold" data-testid="public-quote-number">{quote.quote_number}</p>
              </div>
              <Badge variant="secondary" className={`text-xs ${statusColors[quote.status] || ''}`} data-testid="public-quote-status">{quote.status}</Badge>
            </div>

            <div className="text-sm text-zinc-600 mb-4 space-y-1">
              <p><span className="text-zinc-400">For:</span> {quote.customer_name}</p>
              {quote.address && <p><span className="text-zinc-400">Address:</span> {quote.address}<a href={mapsUrl(quote.address)} target="_blank" rel="noopener noreferrer" className="ml-1 inline-flex align-middle text-zinc-500 hover:text-emerald-700" data-testid="public-quote-map-link" aria-label="Open quote address in Google Maps"><MapPin className="h-3.5 w-3.5" /></a></p>}
              {quote.valid_until && <p><span className="text-zinc-400">Valid until:</span> {quote.valid_until}</p>}
            </div>

            <div className="border border-zinc-200 rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead><tr className="bg-zinc-50 text-xs text-zinc-500 uppercase"><th className="p-2 text-left">Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                <tbody>{quote.line_items?.map((li, i) => (
                  <tr key={i} className="border-t border-zinc-100"><td className="p-2">{li.description}</td><td className="p-2 text-right">{li.quantity}</td><td className="p-2 text-right">${li.unit_price?.toFixed(2)}</td><td className="p-2 text-right font-semibold">${li.total?.toFixed(2)}</td></tr>
                ))}</tbody>
                <tfoot>
                  <tr className="border-t border-zinc-200"><td colSpan="3" className="p-2 text-right text-zinc-500">Subtotal</td><td className="p-2 text-right font-semibold">${quote.subtotal?.toFixed(2)}</td></tr>
                  {quote.tax_amount > 0 && <tr><td colSpan="3" className="p-2 text-right text-zinc-500">Tax ({quote.tax_rate}%)</td><td className="p-2 text-right">${quote.tax_amount?.toFixed(2)}</td></tr>}
                  <tr className="border-t bg-zinc-50"><td colSpan="3" className="p-2 text-right font-bold">Total</td><td className="p-2 text-right text-xl font-bold" data-testid="public-quote-total">${quote.total?.toFixed(2)}</td></tr>
                </tfoot>
              </table>
            </div>

            {quote.notes && <p className="text-xs text-zinc-600 mb-4">{quote.notes}</p>}

            {quote.status === 'accepted' && (
              <div className="p-3 bg-emerald-50 rounded-lg flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-600" /><span className="text-sm font-semibold text-emerald-800">Quote Accepted</span></div>
            )}
            {quote.status === 'declined' && (
              <div className="p-3 bg-red-50 rounded-lg flex items-center gap-2"><XCircle className="w-5 h-5 text-red-500" /><span className="text-sm font-semibold text-red-700">Quote Declined</span></div>
            )}
            {quote.status === 'sent' && (
              <div className="grid grid-cols-2 gap-2" data-testid="public-quote-actions">
                <button onClick={() => respondToQuote('accept')} disabled={!!actionLoading} className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60" data-testid="public-quote-accept-button">
                  {actionLoading === 'accept' ? 'Accepting...' : 'Accept Quote'}
                </button>
                <button onClick={() => respondToQuote('decline')} disabled={!!actionLoading} className="rounded-md border border-zinc-300 px-3 py-2 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60" data-testid="public-quote-decline-button">
                  {actionLoading === 'decline' ? 'Declining...' : 'Decline'}
                </button>
              </div>
            )}
            {message && <p className="mt-3 rounded-md bg-zinc-50 px-3 py-2 text-center text-xs text-zinc-600" data-testid="public-quote-response-message">{message}</p>}

            {business?.contact_phone && (
              <p className="text-xs text-zinc-400 text-center mt-4">Questions? Call {formatPhone(business.contact_phone)}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
